import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, ExternalLink, RefreshCw } from 'lucide-react';
import api from '../api';

const CATEGORIE = ['SaaS','Hosting','AI','Design','Marketing','Comunicazione','Finanza','Altro'];
const FREQUENZE = ['mensile','annuale','semestrale','trimestrale'];

const CAT_COLOR = {
  SaaS:'var(--cyan)', Hosting:'var(--purple)', AI:'var(--yellow)', Design:'#ec4899',
  Marketing:'var(--green)', Comunicazione:'#3b82f6', Finanza:'var(--yellow)', Altro:'var(--text-muted)',
};

const EMPTY = { nome:'', categoria:'SaaS', costo:'', valuta:'EUR', frequenza:'mensile', auto_renew:false, data_inizio:'', data_scadenza:'', url:'', note:'', stato:'attivo' };

function fmt_date(d) {
  if (!d) return null;
  // d può essere "2026-07-15" o ISO string
  const s = typeof d === 'string' ? d.slice(0,10) : d;
  const [y,m,day] = s.split('-');
  const mesi = ['gen','feb','mar','apr','mag','giu','lug','ago','set','ott','nov','dic'];
  return `${parseInt(day)} ${mesi[parseInt(m)-1]} ${y}`;
}

function giorni_scadenza_label(g) {
  if (g == null) return null;
  if (g < 0) return { txt: 'Scaduto', color: 'var(--red)' };
  if (g <= 7) return { txt: `${g}g alla scadenza`, color: 'var(--red)' };
  if (g <= 30) return { txt: `${g}g alla scadenza`, color: 'var(--yellow)' };
  return null;
}

function giorni_addebito_label(d) {
  if (!d) return null;
  const diff = Math.round((new Date(d) - new Date()) / 86400000);
  if (diff <= 0) return { txt: 'oggi', color: 'var(--red)' };
  if (diff <= 3) return { txt: `fra ${diff}g`, color: 'var(--red)' };
  if (diff <= 7) return { txt: `fra ${diff}g`, color: 'var(--yellow)' };
  return { txt: `fra ${diff}g`, color: 'var(--text-dim)' };
}

export default function Abbonamenti() {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filtro, setFiltro] = useState('attivo');

  const load = () => api.get('/abbonamenti').then(r => setItems(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditItem(null); setModalOpen(true); };
  const openEdit = (a) => {
    setForm({ ...a, costo: a.costo || '', data_inizio: a.data_inizio || '', data_scadenza: a.data_scadenza || '', url: a.url || '', note: a.note || '', auto_renew: a.auto_renew || false });
    setEditItem(a); setModalOpen(true);
  };

  const submit = async () => {
    if (!form.nome.trim()) return;
    const payload = { ...form, costo: parseFloat(form.costo) || 0 };
    if (editItem) await api.put(`/abbonamenti/${editItem.id}`, payload);
    else await api.post('/abbonamenti', payload);
    setModalOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm('Eliminare questo abbonamento?')) return;
    await api.delete(`/abbonamenti/${id}`); load();
  };

  const filtered = items.filter(a => !filtro || a.stato === filtro);
  const attivi = items.filter(a => a.stato === 'attivo');
  const totMensile = attivi.reduce((s, a) => s + Number(a.costo_mensile || 0), 0);
  const totAnnuale = totMensile * 12;

  const gruppi = CATEGORIE.map(c => ({ cat:c, items:filtered.filter(a=>a.categoria===c) })).filter(g=>g.items.length>0);
  const altri = filtered.filter(a => !CATEGORIE.includes(a.categoria));
  if (altri.length) gruppi.push({ cat:'Altro', items:altri });

  return (
    <div className="eden-page">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, margin:'0 0 3px', color:'var(--text)' }}>Abbonamenti</h1>
          <p style={{ fontSize:12, color:'var(--text-muted)', margin:0 }}>Servizi attivi, addebiti ricorrenti e costi mensili</p>
        </div>
        <button onClick={openNew} className="eden-btn-primary" style={{ fontSize:12, whiteSpace:'nowrap' }}>
          <Plus size={14}/> Nuovo
        </button>
      </div>

      {/* Sommario costi */}
      {attivi.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10, marginBottom:24 }}>
          {[
            { label:'Costo mensile totale', val:`€ ${totMensile.toFixed(2)}`, color:'var(--cyan)' },
            { label:'Costo annuale stimato', val:`€ ${totAnnuale.toFixed(2)}`, color:'var(--purple)' },
            { label:'Abbonamenti attivi', val: attivi.length, color:'var(--green)' },
          ].map(c => (
            <div key={c.label} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px' }}>
              <div style={{ fontSize:18, fontWeight:700, color:c.color }}>{c.val}</div>
              <div style={{ fontSize:10.5, color:'var(--text-muted)', marginTop:2 }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filtro stato */}
      <div style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap' }}>
        {['attivo','sospeso','cancellato',''].map(s => (
          <button key={s} onClick={() => setFiltro(s)}
            style={{ fontSize:11, padding:'5px 12px', borderRadius:6, border:'1px solid var(--border)', background: filtro===s ? 'var(--cyan-dim)' : 'transparent', color: filtro===s ? 'var(--cyan)' : 'var(--text-muted)', cursor:'pointer' }}>
            {s || 'Tutti'} ({items.filter(a=>!s||a.stato===s).length})
          </button>
        ))}
      </div>

      {/* Lista per categoria */}
      {gruppi.map(g => (
        <div key={g.cat} style={{ marginBottom:24 }}>
          <div style={{ fontSize:10.5, fontWeight:700, color:CAT_COLOR[g.cat]||'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>
            {g.cat}
          </div>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
            {g.items.map((a, i) => {
              const scad = giorni_scadenza_label(a.giorni_alla_scadenza);
              const addebito = giorni_addebito_label(a.prossimo_addebito);
              return (
                <div key={a.id} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 60px', gap:10, alignItems:'center', padding:'13px 16px', borderBottom: i<g.items.length-1 ? '1px solid var(--border)' : 'none' }}>

                  {/* Nome + badge auto-renew */}
                  <div>
                    <div style={{ fontWeight:600, fontSize:13, color: a.stato!=='attivo' ? 'var(--text-dim)' : 'var(--text)', display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                      {a.nome}
                      {a.auto_renew && (
                        <span title="Auto-renew attivo" style={{ display:'inline-flex', alignItems:'center', gap:3, fontSize:10, padding:'1px 6px', borderRadius:10, background:'var(--cyan-dim)', color:'var(--cyan)' }}>
                          <RefreshCw size={9}/> auto
                        </span>
                      )}
                      {a.url && (
                        <a href={a.url} target="_blank" rel="noreferrer" style={{ color:'var(--text-dim)', display:'flex' }}>
                          <ExternalLink size={11}/>
                        </a>
                      )}
                    </div>
                    {a.note && <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:2 }}>{a.note}</div>}
                  </div>

                  {/* Costo */}
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>
                      {a.valuta} {Number(a.costo||0).toFixed(2)}
                    </div>
                    <div style={{ fontSize:10.5, color:'var(--text-dim)', textTransform:'capitalize' }}>
                      {a.frequenza}
                      {a.frequenza !== 'mensile' && ` · ≈€${Number(a.costo_mensile||0).toFixed(2)}/m`}
                    </div>
                  </div>

                  {/* Data addebito / scadenza */}
                  <div>
                    {a.auto_renew ? (
                      <>
                        {a.prossimo_addebito
                          ? <div style={{ fontSize:12, color:'var(--text-muted)' }}>{fmt_date(a.prossimo_addebito)}</div>
                          : <div style={{ fontSize:11, color:'var(--text-dim)' }}>—</div>
                        }
                        {addebito && (
                          <div style={{ fontSize:10.5, color: addebito.color, marginTop:1 }}>prossimo addebito {addebito.txt}</div>
                        )}
                      </>
                    ) : (
                      <>
                        {a.data_scadenza
                          ? <div style={{ fontSize:12, color:'var(--text-muted)' }}>{fmt_date(a.data_scadenza)}</div>
                          : <div style={{ fontSize:12, color:'var(--text-dim)' }}>—</div>
                        }
                        {scad && <div style={{ fontSize:10.5, color:scad.color, marginTop:1 }}>{scad.txt}</div>}
                      </>
                    )}
                  </div>

                  {/* Stato */}
                  <div>
                    <span style={{ fontSize:10.5, padding:'2px 8px', borderRadius:10,
                      background: a.stato==='attivo' ? 'var(--green-dim)' : a.stato==='sospeso' ? 'var(--yellow-dim)' : 'var(--red-dim)',
                      color: a.stato==='attivo' ? 'var(--green)' : a.stato==='sospeso' ? 'var(--yellow)' : 'var(--red)'
                    }}>{a.stato}</span>
                  </div>

                  {/* Azioni */}
                  <div style={{ display:'flex', gap:4, justifyContent:'flex-end' }}>
                    <button onClick={() => openEdit(a)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:6, borderRadius:6, display:'flex' }}
                      onMouseEnter={e=>e.currentTarget.style.color='var(--cyan)'} onMouseLeave={e=>e.currentTarget.style.color='var(--text-dim)'}>
                      <Pencil size={13}/>
                    </button>
                    <button onClick={() => del(a.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:6, borderRadius:6, display:'flex' }}
                      onMouseEnter={e=>e.currentTarget.style.color='var(--red)'} onMouseLeave={e=>e.currentTarget.style.color='var(--text-dim)'}>
                      <Trash2 size={13}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <div style={{ textAlign:'center', padding:60, color:'var(--text-dim)', fontSize:14 }}>
          Nessun abbonamento trovato.
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:12 }}
          onClick={e => { if(e.target===e.currentTarget) setModalOpen(false); }}>
          <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:16, padding:'24px 20px', width:'min(520px, calc(100vw - 24px))', maxHeight:'90dvh', overflowY:'auto' }}>
            <h2 style={{ fontSize:16, fontWeight:700, margin:'0 0 18px', color:'var(--text)' }}>
              {editItem ? 'Modifica abbonamento' : 'Nuovo abbonamento'}
            </h2>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <input placeholder="Nome *" value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} className="eden-input"/>
              <select value={form.categoria} onChange={e=>setForm(f=>({...f,categoria:e.target.value}))} className="eden-input">
                {CATEGORIE.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:10 }}>
              <input placeholder="Costo" type="number" step="0.01" value={form.costo} onChange={e=>setForm(f=>({...f,costo:e.target.value}))} className="eden-input"/>
              <select value={form.valuta} onChange={e=>setForm(f=>({...f,valuta:e.target.value}))} className="eden-input">
                {['EUR','USD','GBP','CHF'].map(v=><option key={v}>{v}</option>)}
              </select>
              <select value={form.frequenza} onChange={e=>setForm(f=>({...f,frequenza:e.target.value}))} className="eden-input">
                {FREQUENZE.map(f=><option key={f}>{f}</option>)}
              </select>
            </div>

            {/* Auto-renew toggle */}
            <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, cursor:'pointer', userSelect:'none' }}>
              <div onClick={() => setForm(f=>({...f,auto_renew:!f.auto_renew}))} style={{
                width:38, height:22, borderRadius:11, position:'relative', flexShrink:0,
                background: form.auto_renew ? 'var(--cyan)' : 'var(--border)',
                transition:'background 0.2s',
              }}>
                <div style={{
                  position:'absolute', top:3, left: form.auto_renew ? 19 : 3,
                  width:16, height:16, borderRadius:'50%', background:'#fff',
                  transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)',
                }}/>
              </div>
              <span style={{ fontSize:13, color: form.auto_renew ? 'var(--cyan)' : 'var(--text-muted)', display:'flex', alignItems:'center', gap:5 }}>
                <RefreshCw size={12}/> Auto-renew (addebito ricorrente automatico)
              </span>
            </label>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <div>
                <label style={{ fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Data inizio *</label>
                <input type="date" value={form.data_inizio} onChange={e=>setForm(f=>({...f,data_inizio:e.target.value}))} className="eden-input"/>
              </div>
              {!form.auto_renew && (
                <div>
                  <label style={{ fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Data scadenza</label>
                  <input type="date" value={form.data_scadenza} onChange={e=>setForm(f=>({...f,data_scadenza:e.target.value}))} className="eden-input"/>
                </div>
              )}
            </div>

            {form.auto_renew && form.data_inizio && (
              <div style={{ background:'var(--cyan-dim)', border:'1px solid rgba(0,212,255,0.15)', borderRadius:8, padding:'8px 12px', marginBottom:10, fontSize:11.5, color:'var(--cyan)' }}>
                Il prossimo addebito viene calcolato automaticamente dalla data di inizio e dalla frequenza.
              </div>
            )}

            <input placeholder="URL" value={form.url} onChange={e=>setForm(f=>({...f,url:e.target.value}))} className="eden-input" style={{ marginBottom:10, width:'100%', boxSizing:'border-box' }}/>

            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:10, marginBottom:18 }}>
              <input placeholder="Note" value={form.note} onChange={e=>setForm(f=>({...f,note:e.target.value}))} className="eden-input"/>
              <select value={form.stato} onChange={e=>setForm(f=>({...f,stato:e.target.value}))} className="eden-input">
                {['attivo','sospeso','cancellato'].map(s=><option key={s}>{s}</option>)}
              </select>
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={submit} className="eden-btn-primary">Salva</button>
              <button onClick={() => setModalOpen(false)} className="eden-btn-ghost">Annulla</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
